import { Dialog, makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React from 'react';
import { X } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { Button } from '../../global/forms/Button';
import { Input } from '../../global/forms/Input';
import { Select } from '../../global/forms/Select';

const useDeleteReportClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: 560,
        gap: 16,
        padding: '40px 40px 24px 40px',
        color: theme.colors.neutral[1],
        '& > span:first-child': { ...theme.fonts.h4 },
        '& > span:nth-child(2)': {
            ...theme.fonts.label,
            '& > span': { ...theme.fonts.labelSemiBold },
        },
    },
    buttons: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 16,
    },
});

/**
 * Dialog to export a report.
 * The dialog does not export the report itself, it only informs the parent the user intent.
 *
 * @param props.loading indicates a loading state
 * @param props.title report title
 * @param props.open set the dialog display state
 * @param props.onClose callback triggered when the user clicks on the background, usually it should not be provided to
 *        prevent accidental closes, and the close action can be handled by the `props.onCancel` callback
 * @param props.onCancel callback triggered when the user clicks on the cancel button
 * @param props.onDelete callback triggered when the user clicks on the delete button
 */
export const ExportReport = (props: {
    loading?: boolean;
    title: string;
    open: boolean;
    onClose?: () => void;
    onCancel?: () => void;
    onExport?: (format: 'pdf' | 'excel', filename: string) => void;
}) => {
    const classes = useDeleteReportClasses();
    const { t } = usePrefixTranslation('report', { report: 'dialogs.ExportReport' });
    const [filename, setFilename] = React.useState(props.title);
    const formats = ['pdf', 'excel'] as const;
    const [format, setFormat] = React.useState<typeof formats[number]>('pdf');

    if (props.loading)
        return (
            <Dialog classes={{ paper: classes.root }} open={props.open} onClose={props.onClose ?? props.onCancel}>
                <Skeleton variant='text' width='50%' />
                <Skeleton variant='text' />
                <Skeleton variant='rect' height='78px' />
                <Skeleton variant='rect' height='78px' />
                <div className={classes.buttons}>
                    <Skeleton variant='rect' width='180px' height='50px' />
                    <Skeleton variant='rect' width='180px' height='50px' />
                </div>
            </Dialog>
        );

    return (
        <Dialog classes={{ paper: classes.root }} open={props.open} onClose={props.onClose ?? props.onCancel}>
            <span>{t('title')}</span>
            <span>{t('subtitle')}</span>
            <Input
                theme='primary'
                label={t('label-filename')}
                placeholder={t('placeholder-filename')}
                value={props.title}
                onChange={e => setFilename(e.target.value)}
            />
            <Select
                theme='primary'
                label={t('label-format')}
                placeholder={t('placeholder-format')}
                options={formats.map(f => f.toUpperCase())}
                selected={formats.map(opt => opt === format)}
                onSelected={selected => setFormat(formats.filter((_, i) => selected[i])[0])}
            />
            <div className={classes.buttons}>
                <Button theme='secondary' disabled={props.loading} onClick={props.onCancel}>
                    <X /> {t('button-cancel')}
                </Button>
                <Button theme='primary' disabled={props.loading} onClick={() => props.onExport?.(format, filename)}>
                    {t('button-export')}
                </Button>
            </div>
        </Dialog>
    );
};
